// MomentCard — displays a Spotify-linked moment post
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FeedPost, POST_COLORS, PostColorHex } from '../types';
import { useAudio } from '../contexts/AudioContext';
import { loadYTApi } from '../utils/ytApi';

declare global {
  interface Window {
    YT: {
      Player: new (el: HTMLElement, opts: object) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}
interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  setVolume(vol: number): void;
  getPlayerState(): number;
  getCurrentTime(): number;
  destroy(): void;
}

interface Props {
  post: FeedPost;
  currentUserId?: string | null;
  onResonate: (postId: string) => void;
  onReply: (post: FeedPost) => void;
  onOpen?: (post: FeedPost) => void;
  onNavigateUser?: (username: string) => void;
  onLoginRequired?: () => void;
  onDelete?: (postId: string) => void;
  onUpdate?: (post: FeedPost) => void;
}

export function MomentCard({ post, currentUserId, onResonate, onReply, onOpen, onNavigateUser, onLoginRequired, onDelete, onUpdate }: Props) {
  const { activePostId, setActivePostId, volume, registerStopCallback, deregisterStopCallback, togglePlayCallback } = useAudio();
  const isActive = activePostId === post.id;
  const [resonated, setResonated] = useState(post.has_resonated);
  const [resonateCount, setResonateCount] = useState(post.resonate_count ?? 0);
  const [resonateLoading, setResonateLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playheadPct, setPlayheadPct] = useState<number | null>(null);

  // Edit / delete state
  const isOwner = !!currentUserId && currentUserId === post.user_id;
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editCaption, setEditCaption] = useState(post.caption ?? '');
  const [editColor, setEditColor] = useState<PostColorHex | null>(post.post_color ?? null);
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // YouTube player
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const ytContainerRef = useRef<HTMLDivElement | null>(null);
  const [ytReady, setYtReady] = useState(false);
  const [ytLoading, setYtLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const volumeRef = useRef(volume);

  // Clip window derived values
  const startSec = post.timestamp_start_ms != null ? post.timestamp_start_ms / 1000 : 0;
  const clipSec = post.clip_duration_ms != null
    ? post.clip_duration_ms / 1000
    : 30;

  // Keep volume ref in sync
  useEffect(() => { volumeRef.current = volume; }, [volume]);

  // Apply feed-level volume changes to live player
  useEffect(() => {
    if (ytPlayerRef.current && ytReady) ytPlayerRef.current.setVolume(Math.round(volume * 100));
  }, [volume, ytReady]);

  function stopPoll() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }

  const stopLocalPlayer = useCallback(() => {
    stopPoll();
    setPlaying(false);
    setPlayheadPct(null);
    if (ytPlayerRef.current) {
      try { ytPlayerRef.current.pauseVideo(); } catch { /* gone */ }
    }
  }, []); // eslint-disable-line

  // Stop when context switches to another card
  useEffect(() => {
    if (!isActive) stopLocalPlayer();
  }, [isActive]); // eslint-disable-line

  // Register stop callback for AudioContext
  useEffect(() => {
    registerStopCallback(post.id, stopLocalPlayer);
    return () => deregisterStopCallback(post.id);
  }); // eslint-disable-line

  // Register space bar toggle when this card is active; clear on unmount
  useEffect(() => {
    if (isActive && post.youtube_video_id) {
      togglePlayCallback.current = handlePlay;
    } else if (!isActive && togglePlayCallback.current === handlePlay) {
      togglePlayCallback.current = null;
    }
    return () => {
      if (togglePlayCallback.current === handlePlay) togglePlayCallback.current = null;
    };
  }); // eslint-disable-line

  // Destroy player on unmount
  useEffect(() => {
    return () => {
      stopPoll();
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch { /* gone */ }
        ytPlayerRef.current = null;
      }
    };
  }, []); // eslint-disable-line

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  function startPoll(player: YTPlayer) {
    stopPoll();
    pollRef.current = setInterval(() => {
      if (!player) { stopPoll(); return; }
      try {
        const cur = player.getCurrentTime();
        const elapsed = cur - startSec;
        if (elapsed >= clipSec) {
          player.pauseVideo();
          player.seekTo(startSec, true);
          stopLocalPlayer();
          return;
        }
        setPlayheadPct(Math.max(0, Math.min(100, (elapsed / clipSec) * 100)));
      } catch { stopPoll(); }
    }, 80);
  }

  function createYTPlayer(videoId: string) {
    // The IFrame API replaces the container element, so we need a fresh wrapper.
    // Wipe the container's children and let YT append the iframe into it.
    const container = ytContainerRef.current;
    if (!container) return;
    container.innerHTML = '';
    const target = document.createElement('div');
    container.appendChild(target);

    const player = new window.YT.Player(target, {
      videoId,
      playerVars: { autoplay: 0, controls: 0, disablekb: 1, fs: 0, modestbranding: 1, rel: 0, origin: window.location.origin },
      events: {
        onReady: () => {
          player.setVolume(Math.round(volumeRef.current * 100));
          setYtReady(true);
          setYtLoading(false);
          player.seekTo(startSec, true);
          player.playVideo();
        },
        onStateChange: (e: { data: number }) => {
          if (e.data === window.YT.PlayerState.PLAYING) {
            setPlaying(true);
            startPoll(player);
          } else if (e.data === window.YT.PlayerState.ENDED) {
            stopLocalPlayer();
          }
        },
        onError: () => {
          setYtLoading(false);
          setYtReady(false);
          stopLocalPlayer();
        },
      },
    });
    ytPlayerRef.current = player;
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await fetch(`/api/v1/posts/${post.id}`, { method: 'DELETE' });
      if (res.ok) onDelete?.(post.id);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function handleSaveEdit() {
    setSaving(true);
    try {
      const res = await fetch(`/api/v1/posts/${post.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption: editCaption.trim() || null, post_color: editColor }),
      });
      if (res.ok) {
        const json = await res.json() as { data: FeedPost };
        onUpdate?.({ ...post, ...json.data });
        setEditing(false);
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleResonate() {
    if (resonateLoading) return;
    if (currentUserId === undefined || currentUserId === null) {
      onLoginRequired?.();
      return;
    }
    const next = !resonated;
    setResonated(next);
    setResonateCount(c => c + (next ? 1 : -1));
    setResonateLoading(true);
    try {
      const res = await fetch(`/api/v1/posts/${post.id}/resonate`, { method: 'POST' });
      if (!res.ok) {
        setResonated(!next);
        setResonateCount(c => c + (next ? -1 : 1));
        return;
      }
      const json = await res.json() as { data: { resonated: boolean } };
      setResonated(json.data.resonated);
      setResonateCount(c => {
        const actual = json.data.resonated;
        return actual !== next ? c + (actual ? 1 : -1) : c;
      });
      onResonate(post.id);
    } catch {
      setResonated(!next);
      setResonateCount(c => c + (next ? -1 : 1));
    } finally {
      setResonateLoading(false);
    }
  }

  function handlePlay() {
    if (!post.youtube_video_id) return;

    // Toggle pause/resume if player already exists and is ready
    if (ytPlayerRef.current && ytReady) {
      const state = ytPlayerRef.current.getPlayerState();
      if (state === window.YT.PlayerState.PLAYING) {
        ytPlayerRef.current.pauseVideo();
        stopPoll();
        setPlaying(false);
        setActivePostId(null);
      } else {
        setActivePostId(post.id);
        ytPlayerRef.current.seekTo(startSec, true);
        ytPlayerRef.current.playVideo();
        setPlaying(true);
      }
      return;
    }

    // First play — create the player
    setYtLoading(true);
    setActivePostId(post.id);
    loadYTApi(() => {
      if (!window.YT || !ytContainerRef.current) {
        setYtLoading(false);
        setActivePostId(null);
        return;
      }
      createYTPlayer(post.youtube_video_id!);
    });
  }

  function openSpotify() {
    if (!post.spotify_track_id) return;
    window.open(`https://open.spotify.com/track/${post.spotify_track_id}`, '_blank', 'noopener');
  }

  function openYouTube() {
    const q = encodeURIComponent(`${post.spotify_track_name ?? ''} ${post.spotify_artist ?? ''}`.trim());
    window.open(`https://www.youtube.com/results?search_query=${q}`, '_blank', 'noopener');
  }

  function copyLink() {
    const url = `${window.location.origin}/posts/${post.id}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setMenuOpen(false);
  }

  const authorLabel = post.author_display_name ?? post.author_username ?? 'user';
  const canOpen = !!onOpen;

  return (
    <article style={{ ...cardStyle, cursor: canOpen ? 'pointer' : 'default' }} onClick={canOpen ? () => onOpen(post) : undefined}>
      {/* Author row */}
      <div style={headerStyle}>
        <button
          onClick={post.author_username && onNavigateUser ? (e => { e.stopPropagation(); onNavigateUser(post.author_username!); }) : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, maxWidth: '70%', background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: post.author_username && onNavigateUser ? 'pointer' : 'default' }}
        >
          <Avatar url={post.author_avatar_url} name={authorLabel} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ ...usernameStyle, ...(post.author_username && onNavigateUser ? { color: 'var(--text)' } : {}), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{authorLabel}</span>
              {post.parent_post_id && (
                <span style={replyBadgeStyle}>reply</span>
              )}
            </div>
            <span style={timeStyle}>{formatRelative(post.created_at)}</span>
          </div>
        </button>
        <div style={{ flex: 1 }} />
        {isOwner && (
          <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
              style={{ color: 'var(--text-muted)', padding: '4px 8px', borderRadius: 6, fontSize: 18, lineHeight: 1, letterSpacing: 2 }}
              aria-label="Post options"
            >···</button>
            {menuOpen && (
              <div onClick={e => e.stopPropagation()} style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4,
                background: 'var(--surface2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', minWidth: 120, zIndex: 50,
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              }}>
                <button onClick={() => { setEditing(true); setMenuOpen(false); }} style={menuItemStyle}>Edit</button>
                <button onClick={copyLink} style={menuItemStyle}>Copy link</button>
                <button onClick={() => { setConfirmDelete(true); setMenuOpen(false); }} style={{ ...menuItemStyle, color: 'var(--danger)' }}>Delete</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Track info */}
      {post.spotify_track_name && (
        <div style={trackStyle} onClick={e => e.stopPropagation()}>
          {/* Hidden YouTube player container */}
          <div ref={ytContainerRef} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} />

          <button
            onClick={handlePlay}
            style={playBtnStyle(playing, ytLoading)}
            aria-label={playing ? 'Pause' : 'Play clip'}
            disabled={!post.youtube_video_id || ytLoading}
            title={!post.youtube_video_id ? 'No video available' : ytLoading ? 'Loading…' : playing ? 'Pause' : 'Play clip'}
          >
            {ytLoading ? <LoadingDots /> : playing ? <PauseIcon /> : <PlayIcon />}
          </button>

          <div style={trackInfoStyle}>
            <span style={trackNameStyle}>{post.spotify_track_name}</span>
            <span style={artistStyle}>{post.spotify_artist}</span>

            <PreviewBar
              clipSec={clipSec}
              startSec={startSec}
              totalSec={post.duration_ms != null ? post.duration_ms / 1000 : null}
              playheadPct={playheadPct}
            />

            {/* External links */}
            <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              {post.spotify_track_id && (
                <button onClick={openSpotify} style={spotifyBtnStyle} title="Open in Spotify">
                  <SpotifyIcon />
                  <span>Spotify</span>
                </button>
              )}
              {post.spotify_track_name && (
                <button onClick={openYouTube} style={youtubeBtnStyle} title="Search on YouTube">
                  <YouTubeIcon />
                  <span>YouTube</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Caption */}
      {!editing && post.caption && <p style={captionStyle}>{post.caption}</p>}

      {/* Delete confirm */}
      {confirmDelete && (
        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--text)' }}>Delete this post? This can't be undone.</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setConfirmDelete(false)} style={cancelBtnStyle}>Cancel</button>
            <button onClick={handleDelete} disabled={deleting} style={deleteBtnStyle}>{deleting ? 'Deleting…' : 'Delete'}</button>
          </div>
        </div>
      )}

      {/* Inline edit form */}
      {editing && (
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <textarea
            value={editCaption}
            onChange={e => setEditCaption(e.target.value)}
            placeholder="Caption (optional)"
            rows={3}
            maxLength={280}
            autoFocus
            style={{ resize: 'none', fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Color:</span>
            {POST_COLORS.map(c => (
              <button
                key={c.hex}
                onClick={() => setEditColor(editColor === c.hex ? null : c.hex)}
                title={c.label}
                style={{
                  width: 22, height: 22, borderRadius: '50%', background: c.hex,
                  border: editColor === c.hex ? '2px solid var(--text)' : '2px solid transparent',
                  outline: editColor === c.hex ? `2px solid ${c.hex}` : 'none',
                  outlineOffset: 1, cursor: 'pointer', flexShrink: 0,
                  transform: editColor === c.hex ? 'scale(1.2)' : 'scale(1)',
                  transition: 'transform 0.1s',
                }}
              />
            ))}
            {editColor && (
              <button onClick={() => setEditColor(null)} style={{ fontSize: 11, color: 'var(--text-muted)' }}>Clear</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setEditing(false); setEditCaption(post.caption ?? ''); setEditColor(post.post_color ?? null); }} style={cancelBtnStyle}>Cancel</button>
            <button onClick={handleSaveEdit} disabled={saving} style={saveBtnStyle}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={actionsStyle} onClick={e => e.stopPropagation()}>
        <button
          onClick={handleResonate}
          disabled={resonateLoading}
          style={resonateStyle(resonated)}
          aria-label={resonated ? 'Un-resonate' : 'This hit me'}
        >
          <HeartIcon filled={resonated} />
          {resonateCount > 0 && (
            <span style={{ opacity: 0.75 }}>{resonateCount}</span>
          )}
        </button>
        <button onClick={() => { if (!currentUserId) { onLoginRequired?.(); return; } onReply(post); }} style={replyBtnStyle}>
          <ReplyIcon />
          <span>reply</span>
          {post.reply_count > 0 && (
            <span style={{ marginLeft: 4, opacity: 0.75 }}>{post.reply_count}</span>
          )}
        </button>
        <button onClick={copyLink} style={replyBtnStyle} title="Copy link" aria-label="Copy link">
          <ShareIcon />
        </button>
      </div>
    </article>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Avatar({ url, name }: { url: string | null; name: string | null }) {
  const [failed, setFailed] = React.useState(false);
  const initial = (name ?? '?')[0].toUpperCase();
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={name ?? ''}
        style={avatarStyle}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div style={{ ...avatarStyle, background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600 }}>
      {initial}
    </div>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill={filled ? 'var(--accent)' : 'none'} stroke={filled ? 'var(--accent)' : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}

function ReplyIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 14l-4-4 4-4" />
      <path d="M5 10h11a4 4 0 010 8h-1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5,3 19,12 5,21" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

function LoadingDots() {
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: '#ff6b6b', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

function ShareIcon() {
  return (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function SpotifyIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="12" fill="#1DB954" />
      <path fill="#fff" d="M17.9 10.9C14.7 9 9.35 8.8 6.3 9.75c-.5.15-1-.15-1.15-.6-.15-.5.15-1 .6-1.15 3.55-1.05 9.4-.85 13.1 1.35.45.25.6.85.35 1.3-.25.35-.85.5-1.3.25zm-.1 2.8c-.25.4-.75.5-1.15.25-2.7-1.65-6.8-2.15-9.95-1.15-.4.1-.85-.1-.95-.5-.1-.4.1-.85.5-.95 3.65-1.1 8.15-.55 11.25 1.35.4.25.5.75.3 1zm-1.3 2.7c-.2.3-.6.4-.9.2-2.35-1.45-5.3-1.75-8.8-.95-.35.1-.65-.15-.75-.45-.1-.35.15-.65.45-.75 3.8-.85 7.1-.5 9.7 1.1.3.15.4.55.3.85z"/>
    </svg>
  );
}


function YouTubeIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.75 15.5v-7l6.5 3.5-6.5 3.5z" fill="#FF0000"/>
    </svg>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 'var(--radius)',
  padding: '16px',
  border: '1px solid var(--border)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const avatarStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  objectFit: 'cover',
  flexShrink: 0,
};

const usernameStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 600,
};

const timeStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--text-muted)',
};

const trackStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
  background: 'var(--surface2)',
  borderRadius: 'var(--radius-sm)',
  padding: '10px 12px',
};

const playBtnStyle = (active: boolean, loading?: boolean): React.CSSProperties => ({
  width: 44,
  height: 44,
  borderRadius: 8,
  background: active ? '#ff6b6b' : 'var(--surface)',
  border: `1px solid ${active ? '#ff6b6b' : 'var(--border)'}`,
  color: active ? '#fff' : '#ff6b6b',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 2,
  transition: 'all 0.15s',
  cursor: 'pointer',
});

const trackInfoStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const trackNameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const artistStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// ─── PlayerBar ────────────────────────────────────────────────────────────────
// Full-song timeline showing:
//   • Dimmed track on either side of the clip window
//   • Highlighted clip window in the post color
//   • Animated white playhead moving through the clip while playing
//   • Timestamp labels below (start, clip end, total)
function PreviewBar({
  clipSec,
  startSec,
  totalSec,
  playheadPct,
}: {
  clipSec: number;
  startSec: number | null;
  totalSec: number | null;
  playheadPct: number | null;
}) {
  const hasSong = startSec != null && totalSec != null && totalSec > 0;
  const clipStart = hasSong ? (startSec! / totalSec!) * 100 : 0;
  const clipWidth = hasSong ? Math.min((clipSec / totalSec!) * 100, 100 - clipStart) : 100;
  // Playhead as % of the full bar
  const playheadLeft = playheadPct != null
    ? clipStart + (playheadPct / 100) * clipWidth
    : null;

  return (
    <div style={{ marginTop: 10 }}>
      {/* Pill track */}
      <div style={{ position: 'relative', height: 6, borderRadius: 100, background: 'var(--border)', overflow: 'hidden' }}>
        {/* Clip window gradient */}
        <div style={{
          position: 'absolute',
          top: 0, bottom: 0,
          left: `${clipStart}%`,
          width: `${clipWidth}%`,
          background: 'linear-gradient(90deg, #ff6b6b88, #ff6b6b)',
          borderRadius: 100,
        }} />
        {/* Played portion */}
        {playheadLeft != null && (
          <div style={{
            position: 'absolute',
            top: 0, bottom: 0,
            left: `${clipStart}%`,
            width: `${(playheadLeft - clipStart)}%`,
            background: '#ff6b6b',
            borderRadius: 100,
          }} />
        )}
      </div>

      {/* Playhead dot */}
      {playheadLeft != null && (
        <div style={{
          position: 'relative',
          height: 0,
        }}>
          <div style={{
            position: 'absolute',
            top: -10,
            left: `${playheadLeft}%`,
            transform: 'translateX(-50%)',
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 0 8px #ff6b6b, 0 0 2px #fff',
            pointerEvents: 'none',
          }} />
        </div>
      )}

      {/* Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {hasSong ? formatSec(startSec!) : ''}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#ff6b6b', letterSpacing: '0.02em' }}>
          {clipSec}s clip
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {hasSong ? formatSec(totalSec!) : ''}
        </span>
      </div>
    </div>
  );
}


const captionStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.5,
  color: 'var(--text)',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  paddingTop: 4,
};

const resonateStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  color: active ? 'var(--accent)' : 'var(--text-muted)',
  padding: '6px 10px',
  borderRadius: 100,
  background: active ? 'var(--accent-glow)' : 'transparent',
  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
  cursor: 'pointer',
  transition: 'all 0.15s',
});

const replyBadgeStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, color: 'var(--accent)',
  background: 'var(--accent-glow)', border: '1px solid var(--accent)',
  borderRadius: 100, padding: '1px 6px', letterSpacing: '0.03em',
  flexShrink: 0,
};

const replyBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  color: 'var(--text-muted)',
  padding: '6px 10px',
  borderRadius: 100,
  border: '1px solid var(--border)',
};

const spotifyBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 10,
  fontWeight: 600,
  color: '#1DB954',
  background: 'rgba(29,185,84,0.1)',
  border: '1px solid rgba(29,185,84,0.3)',
  borderRadius: 100,
  padding: '3px 8px',
  cursor: 'pointer',
  width: 'fit-content',
};

const youtubeBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 10,
  fontWeight: 600,
  color: '#ff0000',
  background: 'rgba(255,0,0,0.1)',
  border: '1px solid rgba(255,0,0,0.3)',
  borderRadius: 100,
  padding: '3px 8px',
  cursor: 'pointer',
  width: 'fit-content',
};

const menuItemStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left',
  padding: '9px 14px', fontSize: 13, cursor: 'pointer',
  color: 'var(--text)',
};

const cancelBtnStyle: React.CSSProperties = {
  flex: 1, padding: '8px 0', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer',
};

const deleteBtnStyle: React.CSSProperties = {
  flex: 1, padding: '8px 0', borderRadius: 'var(--radius-sm)',
  background: 'var(--danger)', border: 'none', fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer',
};

const saveBtnStyle: React.CSSProperties = {
  flex: 1, padding: '8px 0', borderRadius: 'var(--radius-sm)',
  background: 'var(--accent)', border: 'none', fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer',
};

// ─── Util ─────────────────────────────────────────────────────────────────────

function formatSec(s: number): string {
  return `${Math.floor(s / 60)}:${String(Math.floor(s) % 60).padStart(2, '0')}`;
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d`;
  return new Date(ts).toLocaleDateString();
}
